import { useEffect, useState, useRef } from 'react';
import './CompanyProfileWidget.css';
import { formatNumber } from '../../utils/utils';
import { getCompanyProfile } from '../../api/company.api';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';

export interface CompanyProfileWidgetProps extends BaseWidgetProps {
  symbol?: string;
  token?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

interface ShowMoreProps {
  text?: string;
  maxChars?: number;
  moreLabel?: string;
  lessLabel?: string;
  className?: string;
}

const CompanyProfileWidget = (props: CompanyProfileWidgetProps) => {
  const { onHeightChange } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(
    containerRef,
    WidgetNameSource.CompanyProfile,
    onHeightChange,
  );
  const [bizsummary, setBizsummary] = useState<string>('-');
  const [companyname, setCompanyname] = useState<string>('-');
  const [incorpdate, setIncorpdate] = useState<string>('-');
  const [sectordesc, setSectordesc] = useState<string>('-');
  const [website, setWebsite] = useState<string>('');
  const [officername, setOfficername] = useState<string>('-');
  const [officertitle, setOfficertitle] = useState<string>('-');
  const [employees, setEmployees] = useState<string>('-');
  const [totalsharesoutstanding, setTotalsharesoutstanding] =
    useState<string>('-');
  const [totalfloat, setTotalfloat] = useState<string>('-');
  const [lastupdatedate, setLastupdatedate] = useState<string>('-');
  const [winwidth, setWinwidth] = useState(window.innerWidth);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'companyProfile');

  const ShowMore: React.FC<ShowMoreProps> = ({
    text = '',
    maxChars = 700,
    moreLabel = 'more',
    lessLabel = '',
    className,
  }) => {
    const [expanded, setExpanded] = useState(false);
    const isLong = text.length > maxChars;
    const shown = expanded || !isLong ? text : text.slice(0, maxChars) + '…';
    const id = '0';

    return (
      <div className={className}>
        <span id={id} aria-expanded={expanded}>
          {shown}
        </span>
        {isLong && (
          <a
            onClick={() => setExpanded((s) => !s)}
            aria-controls={id}
            aria-expanded={expanded}
          >
            {expanded ? lessLabel : moreLabel}
          </a>
        )}
      </div>
    );
  };

  useEffect(() => {
    const handleResize = () => setWinwidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [winwidth]);

  useEffect(() => {
    setIsLoading(true);
    setIsEmptyData(false);
    getCompanyProfile(props.symbol || '', props.token || '')
      .then((res) => {
        if (!res) {
          setIsEmptyData(true);
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'CompanyProfileWidget',
            data: null,
          });
          setIsLoading(false);
          return;
        }
        if (res.bizsummary) {
          setBizsummary(res.bizsummary);
        }

        if (res.companyname) {
          setCompanyname(res.companyname);
        }

        if (res.incorpdate) {
          setIncorpdate(res.incorpdate);
        }

        if (res.sectordesc) {
          setSectordesc(res.sectordesc);
        }

        //the backend sends "-" when there is no website; treat it as empty so no
        //link is rendered, and reset so a previous symbol's website doesn't linger
        const cleanedWebsite = String(res.website || '')
          .trim()
          .replace(/^https?:\/\//i, '')
          .replace(/\/$/, '');
        setWebsite(cleanedWebsite === '-' ? '' : cleanedWebsite);

        if (res.officername) {
          setOfficername(res.officername);
        }

        if (res.officertitle) {
          setOfficertitle(res.officertitle);
        }

        if (res.employees) {
          setEmployees(res.employees);
        }

        if (res.totalsharesoutstanding) {
          setTotalsharesoutstanding(res.totalsharesoutstanding);
        }

        if (res.totalfloat) {
          setTotalfloat(res.totalfloat);
        }

        if (res.lastupdatedate) {
          setLastupdatedate(res.lastupdatedate);
        }

        setIsEmptyData(res.total === 0);
        if (res.total === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'CompanyProfileWidget',
            data: null,
          });
        }
      })
      .catch((err) => {
        console.log(err);
        props.onError?.({
          message: err.message,
          type: err.name + '_' + err.code,
        });
        setIsEmptyData(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [props.symbol]);

  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div className="companyprofile" ref={containerRef}>
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="flex-container">
            <div className="companyprofile-bizsummary">
              {winwidth < 768 ? (
                <ShowMore text={bizsummary} />
              ) : (
                <span>{bizsummary}</span>
              )}
            </div>

            <div className="companyprofile-separator"></div>

            <div className="companyprofile-profile">
              <table className="companyprofile-table">
                <tbody>
                  <tr>
                    <td>Company name</td>
                    <td>{companyname}</td>
                  </tr>

                  <tr>
                    <td>Incorporated date</td>
                    <td>{incorpdate}</td>
                  </tr>

                  <tr>
                    <td>Sector (TRBC)</td>
                    <td>{sectordesc}</td>
                  </tr>

                  <tr>
                    <td>Website</td>
                    <td>
                      {website ? <a href={`https://${website}`} target="_blank" rel="noreferrer" className="website">{website}</a> : '-'}
                    </td>
                  </tr>

                  <tr>
                    <td>Officer</td>
                    <td>{officername}</td>
                  </tr>

                  <tr>
                    <td>Title of officer</td>
                    <td>{officertitle}</td>
                  </tr>

                  <tr>
                    <td>Employees</td>
                    <td>{formatNumber(employees, 0, 0, false)}</td>
                  </tr>

                  <tr>
                    <td>Outstanding shares</td>
                    <td>{formatNumber(totalsharesoutstanding, 0, 0, false)}</td>
                  </tr>

                  <tr>
                    <td>Floating shares</td>
                    <td>{formatNumber(totalfloat, 0, 0, false)}</td>
                  </tr>

                  <tr>
                    <td>Last update</td>
                    <td>{lastupdatedate}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CompanyProfileWidget;
